import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import { PLATFORM_ANALYTICS_DASHBOARD_TYPE, PLATFORM_ANALYTICS_KPI_PRESETS } from '../dashboard/widgetCatalog';

const DASHBOARD_TYPE = PLATFORM_ANALYTICS_DASHBOARD_TYPE;

export default function PlatformDashboardConfigPage() {
  const { user } = usePlatformAuth();
  const canManage = user && user.role === 'super_admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({
    widget_code: '',
    widget_name: '',
    config_json: '',
    is_enabled: true,
    preset: ''
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(
        `/api/platform/dashboard/widget-configs?dashboard_type=${encodeURIComponent(DASHBOARD_TYPE)}`
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load configuration');
      setItems(json.items || []);
    } catch (e) {
      setError(e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      widget_code: '',
      widget_name: '',
      config_json: '',
      is_enabled: true,
      preset: ''
    });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      widget_code: row.widget_code,
      widget_name: row.widget_name,
      config_json: row.config_json ? JSON.stringify(row.config_json, null, 2) : '',
      is_enabled: row.is_enabled,
      preset: ''
    });
    setDialogOpen(true);
  };

  const saveDialog = async () => {
    setSaving(true);
    setError(null);
    try {
      let config_json;
      const raw = form.config_json.trim();
      if (raw) {
        config_json = JSON.parse(raw);
        if (typeof config_json !== 'object' || config_json === null || Array.isArray(config_json)) {
          throw new Error('config_json must be a JSON object');
        }
      }
      const body = {
        dashboard_type: DASHBOARD_TYPE,
        widget_code: form.widget_code.trim(),
        widget_name: form.widget_name.trim(),
        is_enabled: form.is_enabled,
        ...(config_json !== undefined ? { config_json } : {})
      };
      if (editing) {
        const patch = {
          widget_name: body.widget_name,
          is_enabled: body.is_enabled
        };
        if (raw) patch.config_json = config_json;
        else patch.config_json = null;
        const res = await platformApiFetch(`/api/platform/dashboard/widget-configs/${editing.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch)
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Update failed');
      } else {
        const res = await platformApiFetch('/api/platform/dashboard/widget-configs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Create failed');
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (row) => {
    try {
      const res = await platformApiFetch(`/api/platform/dashboard/widget-configs/${row.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_enabled: !row.is_enabled })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Update failed');
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const reorder = async (ordered) => {
    try {
      const res = await platformApiFetch('/api/platform/dashboard/widget-configs/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dashboard_type: DASHBOARD_TYPE,
          ordered_ids: ordered.map((r) => r.id)
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Reorder failed');
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const move = (index, dir) => {
    const next = [...items];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    reorder(next);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await platformApiFetch(`/api/platform/dashboard/widget-configs/${deleteTarget.id}`, {
        method: 'DELETE'
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Platform analytics KPI strip (<code>{DASHBOARD_TYPE}</code>). Super admins can add, reorder, and remove KPI
        cards. Analysts can view this list. When no widgets are configured, the original KPI layout is used.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {canManage && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add widget
          </Button>
        </Box>
      )}

      <Paper sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={100}>Order</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No custom KPI widgets — default analytics KPI row is active.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row, index) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {canManage && (
                        <>
                          <Tooltip title="Move up">
                            <span>
                              <IconButton size="small" disabled={index === 0} onClick={() => move(index, -1)}>
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                size="small"
                                disabled={index === items.length - 1}
                                onClick={() => move(index, 1)}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      {canManage ? (
                        <Switch checked={row.is_enabled} onChange={() => toggleEnabled(row)} size="small" />
                      ) : row.is_enabled ? (
                        'Yes'
                      ) : (
                        'No'
                      )}
                    </TableCell>
                    <TableCell>
                      <code>{row.widget_code}</code>
                    </TableCell>
                    <TableCell>{row.widget_name}</TableCell>
                    <TableCell align="right">
                      {canManage && (
                        <>
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit widget' : 'Add widget'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {!editing && (
            <TextField
              select
              label="Preset (optional)"
              value={form.preset}
              fullWidth
              size="small"
              onChange={(e) => {
                const v = e.target.value;
                const p = PLATFORM_ANALYTICS_KPI_PRESETS.find((x) => x.code === v);
                setForm((f) => ({
                  ...f,
                  preset: v,
                  ...(p ? { widget_code: p.code, widget_name: p.name } : {})
                }));
              }}
            >
              <MenuItem value="">— None —</MenuItem>
              {PLATFORM_ANALYTICS_KPI_PRESETS.map((p) => (
                <MenuItem key={p.code} value={p.code}>
                  {p.code} — {p.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label="Widget code"
            value={form.widget_code}
            onChange={(e) => setForm((f) => ({ ...f, widget_code: e.target.value }))}
            fullWidth
            size="small"
            required
            disabled={!!editing}
            helperText="Lowercase slug matching a KPI renderer, e.g. kpi_tenant_count"
          />
          <TextField
            label="Display name"
            value={form.widget_name}
            onChange={(e) => setForm((f) => ({ ...f, widget_name: e.target.value }))}
            fullWidth
            size="small"
            required
          />
          <TextField
            label="config_json (optional)"
            value={form.config_json}
            onChange={(e) => setForm((f) => ({ ...f, config_json: e.target.value }))}
            fullWidth
            size="small"
            multiline
            minRows={3}
            placeholder='{ "span": { "xs": 6, "sm": 4, "md": 2 } }'
          />
          <FormControlLabel
            control={
              <Switch checked={form.is_enabled} onChange={(e) => setForm((f) => ({ ...f, is_enabled: e.target.checked }))} />
            }
            label="Enabled"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveDialog} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Remove widget?</DialogTitle>
        <DialogContent>
          {deleteTarget && (
            <Typography>
              Remove <strong>{deleteTarget.widget_name}</strong> (<code>{deleteTarget.widget_code}</code>) from this
              dashboard?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
